<template>
  <div>
    <div class="flex flex-wrap items-center justify-between pb-4 py-1">
      <div class="flex items-center space-x-4">
        <div class="relative">
          <button
            id="dropdownRadioButton"
            class="inline-flex items-center text-gray-400 bg-white border border-gray-300 rounded-md px-6 py-2"
            type="button"
            @click="toggleDropdown"
          >
            <svg
              class="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-width="2"
                d="M18.796 4H5.204a1 1 0 0 0-.753 1.659l5.302 6.058a1 1 0 0 1 .247.659v4.874a.5.5 0 0 0 .2.4l3 2.25a.5.5 0 0 0 .8-.4v-7.124a1 1 0 0 1 .247-.659l5.302-6.059c.566-.646.106-1.658-.753-1.658Z"
              />
            </svg>
            {{ selectedFilter ? formatLabel(selectedFilter) : "Filters" }}
            <svg
              class="w-2.5 h-2.5 ms-2.5"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 10 6"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m1 1 4 4 4-4"
              />
            </svg>
          </button>

          <!-- Dropdown menu -->
          <div
            v-if="isDropdownVisible"
            class="absolute z-10 mt-2 bg-white border border-gray-300 rounded-md shadow-lg w-40"
          >
            <ul class="py-2 text-sm text-gray-700">
              <li>
                <button
                  class="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  @click="applyFilter('brandName')"
                >
                  Brand Name
                </button>
              </li>
              <li>
                <button
                  class="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  @click="applyFilter('genericName')"
                >
                  Generic Name
                </button>
              </li>
              <li>
                <button
                  class="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  @click="applyFilter('code')"
                >
                  Code
                </button>
              </li>
            </ul>
          </div>
          <!-- start dropdown filter -->
          <template v-if="currentTab === 'inventory-queue'">
            <div
              v-show="isDropdownOpen"
              id="filterDropdown"
              class="absolute z-10 mt-2 w-80 p-4 bg-white border border-gray-300 rounded-lg shadow-lg"
            >
              <div class="flex">
                <button
                  type="button"
                  class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow"
                  @click="closeDropdown"
                  aria-label="Close"
                >
                  <span class="sr-only">Dismiss</span>
                  <svg
                    class="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                </button>
              </div>
              <form class="max-w-sm mx-auto" @submit.prevent="applyFilters">
                <div class="flex justify-end space-x-2">
                  <button
                    type="button"
                    @click="cancelFilters"
                    class="px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-lg hover:bg-custom-yellow-dark"
                  >
                    Filter
                  </button>
                </div>
              </form>
            </div>
          </template>
        </div>

        <div class="relative flex">
          <input
            type="text"
            v-model="searchQuery"
            id="table-search"
            class="px-4 py-2 text-gray-700 border border-gray-300 rounded-sm dark:border-gray-300 dark:placeholder-gray-400"
            placeholder="Search"
          />
          <button
            @click="triggerSearch"
            class="bg-custom-yellow text-white rounded-r px-2 md:px-3 py-0 md:py-1"
          >
            <svg
              class="w-6 h-6 text-gray-100 dark:text-white"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-width="2"
                d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
              />
            </svg>
          </button>
        </div>
      </div>

      <div class="flex items-center space-x-4 mt-4 sm:mt-0">
        <template v-if="currentTab === 'inventory-queue'">
          <CustomButton
            label="Add Inventory Item"
            :icon-path="'M5 12h14m-7 7V5'"
            bg-class="bg-custom-yellow"
            @click="handleAddInventoryItems"
          />
          <CustomButton
              label="Upload Inventory Items"
              :icon-path="'M5 12h14m-7 7V5'"
              bg-class="bg-custom-yellow"
              @click="handleUploadInventoryItems"
          />
        </template>
      </div>
    </div>
  </div>

  <div class="tabs flex border-b mb-4">
    <button
      class="px-4 py-2 -mb-px font-semibold border-b-2"
      :class="{
        'border-custom-yellow text-custom-blue':
          currentTab === 'inventory-queue',
        'text-gray-500': currentTab !== 'inventory-queue',
      }"
      @click="currentTab = 'inventory-queue'"
    >
      Inventory Items
    </button>
  </div>

  <component
    :is="currentTabComponent"
    ref="currentTabComponent"
    :search-query="searchQuery"
  ></component>
  <CategorySelectionModal
    :show="showCategorySelectionModal"
    @close="closeCategorySelectionModal"
    @submit="handlePatientSubmit"
  />

  <UploadInventoryItemsModal :show="showUploadInventoryItems" @close="showUploadInventoryItems = false"/>
</template>

<script>
import CustomButton from "@/components/CustomButton.vue";
import InventoryQueueTab from "../tabs/InventoryQueueTab.vue";
import CategorySelectionModal from "../modals/CategorySelectionModal.vue";
// import CategorySelectionModal from "../modals/CategorySelectionModal.vue";
import StockQueueTab from "../tabs/StockQueueTab.vue";
import UploadInventoryItemsModal from "@/views/modals/inventory/UploadInventoryItemsModal.vue";

export default {
  name: "InventoryDashboardPage",
  components: {
    UploadInventoryItemsModal,
    CustomButton,
    InventoryQueueTab,
    CategorySelectionModal,
    StockQueueTab,
  },
  data() {
    return {
      currentTab: "inventory-queue",
      showCategorySelectionModal: false,
      searchQuery: "",
      isDropdownVisible: false,
      selectedFilter: null,
      showUploadInventoryItems: false,
    };
  },
  computed: {
    currentTabComponent() {
      switch (this.currentTab) {
        case "inventory-queue":
          return "InventoryQueueTab";
        case "stock-queue":
          return "StockQueueTab";
        default:
          return "InventoryQueueTab";
      }
    },
  },
  methods: {
    handleUploadInventoryItems() {
      this.showUploadInventoryItems = true;
    },
    handleCategorySelectionModal() {
      this.showCategorySelectionModal = true;
    },
    handleAddInventoryItems() {
      this.$router.push({ name: "InventoryItemsPage" });
    },
    formatLabel(key) {
      return key
        .split(/(?=[A-Z])/)
        .join(" ")
        .replace(/^\w/, (c) => c.toUpperCase());
    },
    toggleDropdown() {
      this.isDropdownVisible = !this.isDropdownVisible;
    },
    applyFilter(option) {
      this.selectedFilter = option;
      this.isDropdownVisible = false; // Close dropdown after selection
    },
    async triggerSearch() {
      if (this.selectedFilter === null) {
        this.selectedFilter = "search";
      }
      const params = {
        [this.selectedFilter]: this.searchQuery,
      };
      console.log("search params:", params);
      this.$refs.currentTabComponent.searchInventoryItems(params);
    },
  },
};
</script>

<style lang="css" scoped></style>
